import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Analysis.scss";
import { Suggestions } from "../../Interfaces/Interfaces";
import contractAnimation from '../../Assets/lottie/loading.json';
import { ReactSVG } from "react-svg";
import thumbsDown from '../../Assets/icons/thumbs-down.svg';
import owl from '../../Assets/icons/owl.png';
import { Trans, useTranslation } from "react-i18next";
import LottieAnimation from "../../Components/Animation/LottieAnimation";
import SelectLanguage from "../../Components/Select/SelectLanguage";
import SelectJurisdiction from "../../Components/Select/SelectJurisdiction";
import SelectType from "../../Components/Select/SelectType";
import SelectDocuments from "../Training/SelectDocuments";
import { useSelector } from "react-redux";
import { selectConfig, selectResellerConfig } from "../../Store/configSlice";
import ApiContext from "../../Api/ApiContext";
import useAnalytics from "../../Analytics/Analytics";
import MultipleQuestions from "../Discovery/MultipleQuestions";
import Tooltip, { ETooltipPosition } from "../../Components/Tooltip/TooltipOnRef";
import infoIcon from '../../Assets/icons/info-circle.svg';
import starsIcon from '../../Assets/icons/stars.svg';
import draftIcon from '../../Assets/icons/redraft.svg';
import CreditsInsufficient from "../../Components/Reseller/CreditsInsufficient/CreditsInsufficient";
import RestrictReadOnly from "../../Helpers/RestrictReadOnly";
import Button from "../../Components/Buttons/Button";

interface AnalysisProps {
  id: string;
  navigateToCopilot: (prompt: string) => void;
  display: boolean;
  doc: any;
  partyState: string;
  setPartyState: (partyState: string) => void;
  typeState: string;
  setTypeState: (typeState: string) => void;
  languageState: string;
  setLanguageState: (languageState: string) => void;
  jurisdictionState: string;
  setJurisdictionState: (jurisdictionState: string) => void;
  checklist: Array<string>;
  setChecklist: (checklist: Array<string>) => void;
  selectedTrainingDocuments: any;
  setSelectedTrainingDocuments: any;
  trainingFilter: any;
  setTrainingFilter: any;
  showTrainingModal: boolean;
  setShowTrainingModal: (showTrainingModal: boolean) => void;
  reviewing: boolean;
  setReviewing: (reviewing: boolean) => void;
  t: any;
  setT: any;
}

interface ReasonType {
  riskLevel: number;
  snippetFromContract: string;
  reason: string;
  reasonExplained: string;
  law?: string;
  type: string;
  id: string;
  contractSectionOrArticle: string;
  emoji: string;
}

const Analysis: React.FC<AnalysisProps> = ({ 
  id, navigateToCopilot, display, doc, partyState, setPartyState, typeState, setTypeState, languageState, 
  setLanguageState, jurisdictionState, setJurisdictionState, checklist, setChecklist, selectedTrainingDocuments, setSelectedTrainingDocuments, trainingFilter, setTrainingFilter, showTrainingModal, setShowTrainingModal, reviewing, setReviewing, t, setT 
}) => {

    const [ response, setResponse ] = useState<Suggestions>({checklistArray: [], issueArray: [], remarkArray: [], legalComplianceArray: [], businessConditionsArray: [], deletedArray: []});
    const [ finished, setFinished ] = useState<boolean>(false);

    const [ selectedReason, setSelectedReason ] = useState(null);

    const [ contract, setContract ] = useState(doc);
    const [ parties, setParties ] = useState([]);

    const { t: tGlobal } = useTranslation();

    const resellerConfig = useSelector(selectResellerConfig);

    useEffect(() => {
      if(doc !== contract) setContract(doc);

      if(doc?.parties) {
        setParties(JSON.parse(doc.parties));
      }
    }, [doc, contract]);

    const [ fetchingData, setFetchingData ] = useState(true);

    const api = useContext(ApiContext);

    useEffect(() => {

      if(!id) return;

        let interval = null;
  
        api.post('/suggestions',
          { documentId: id }
        ).then(res => {
            if(res.data.document?.status === "VALIDATED") {
              setFinished(true);
            }
            setResponse(res.data.suggestions);
            setFetchingData(false);
        });
      
        const fetchSuggestions = async () => {
          try {
            const { data } = await api.post(
              '/suggestions', 
              { documentId: id }
            );
            if(data.suggestions.issueArray?.length > 0 || data.suggestions.remarkArray?.length > 0 || data.suggestions.LegalComplianceArray?.length > 0 || data.suggestions.businessConditionsArray?.length > 0 ||data.suggestions.deletedArray?.length > 0 || data.suggestions.checklistArray?.length > 0) {
              
              setResponse(data.suggestions);
              if(data.document.status === "VALIDATED") {
                setFinished(true);
                clearInterval(interval);
              }
            }
          } catch (error) {
            console.error(error);
          }
        };

        interval = setInterval(fetchSuggestions, 5000);
      
        return () => {
          clearInterval(interval);
        };
    }, [id, api]);

    async function deleteSuggestion(suggestionId) {
          api.post('/delete-suggestion',
            { suggestionId }
          ).then(res => {
              setResponse(res.data.suggestions);
          })
    }

    useEffect(() => {
        if (selectedReason) {
          const selected = document.querySelector('.selected');
          const highlighted = document.querySelector('.highlighted') as HTMLElement;;
          const highlightedParent = document.querySelector('.contractContent') as HTMLElement;;
    
          if (selected) {
            selected.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
    
          if (highlighted) {
            const offset = highlighted.offsetTop - ((highlightedParent.offsetHeight / 2) - (highlighted.offsetHeight / 2));
            highlightedParent.scrollTo({ top: offset, behavior: 'smooth' });
          }
        }
      }, [selectedReason]);
        
      function highlight(reason: ReasonType) {
    
        setSelectedReason(reason);
    
        Array.from(document.getElementsByClassName('highlighted')).forEach(element => {
            element.classList.remove('highlighted');
    
            const riskLevelClass = getRiskClass(reason.riskLevel);
            if (element.classList.contains(riskLevelClass)) {
                element.classList.remove(riskLevelClass);
            }
        });
    
        if(reason.snippetFromContract === "GENERAL") return;
    
        const elementID = reason.snippetFromContract;
    
        let element = document.getElementById(elementID);
    
        if (element) {
            element.classList.add("highlighted");
            element.classList.add(getRiskClass(reason.riskLevel));
        }
      } 
        
      function getRiskClass(riskLevel: number) {
        if (riskLevel < 5) return 'low';
        if (riskLevel < 8) return 'medium';
        return 'high';
      }

      function copilot(type, reason) {
        const action = type === "explain" ? t('analysis.explain_prompt') : t('analysis.redraft_prompt');
        const prompt = `${t('analysis.copilot_1')}<br/><br/><strong>${reason.reason}</strong><br/><i>${reason.reasonExplained}</i><br/><br/>${action}`;
        navigateToCopilot(prompt);
      }
        
      const Reason = ({reason}) => {

        return (
          <div className={'reason ' + (reason?.reason === selectedReason?.reason ? "selected " : "not-selected ") + (reason.snippetFromContract !== "GENERAL" ? "snippet " : "") + getRiskClass(reason.riskLevel)}>
            <div className="risk">{reason.riskLevel}</div>
            { reason.snippetFromContract !== "GENERAL" && <div className={ "goTo " + getRiskClass(reason.riskLevel) } onClick={() => highlight(reason)} data-click-target="highlight" data-click-source="review-item" >{ reason.contractSectionOrArticle }</div> }
            <div className="title">{reason.emoji} {reason.reason}</div>
            <div className="law">{reason.law}</div>
            <div className="reasonExplained">{reason.reasonExplained}</div>
            
            <RestrictReadOnly>
              <div className="copilot">
                  <div className="explain" onClick={() => copilot("explain", reason)} data-click-target="copilot" data-click-source="review-item">
                    <ReactSVG src={starsIcon} /> {t('analysis.explain')}
                  </div>
                  <div className="redraft" onClick={() => copilot("redraft", reason)} data-click-target="copilot" data-click-source="review-item">
                    <ReactSVG src={draftIcon} />{t('analysis.redraft')}
                  </div>
              </div>
              { reason.type !== "DELETED" && <div className="delete" onClick={() => deleteSuggestion(reason.id)} data-click-target="thumbs-down" data-click-source="review-item" >
                <ReactSVG src={thumbsDown} />
              </div> }
            </RestrictReadOnly>
          </div>
        );
      }
        
      const Issues = () => {
    
        if (response?.issueArray?.length > 0) return (
          <div>
            <h3 className="dropdown red">{t('analysis.issues')} <span className="itemCount">{t('analysis.items', { count: response?.issueArray?.length || 0 })}</span></h3>
            <div className="issues">
              { response?.issueArray?.map((reason, index) => <Reason key={index} reason={reason} />) }
            </div>
          </div>
        );
    
        return null;
      }
        
      const Remarks = () => {
    
        if (response?.remarkArray?.length > 0) return (
          <div>
            <h3 className="dropdown blue">{t('analysis.remarks')} <span className="itemCount">{t('analysis.items', { count: response?.remarkArray?.length || 0 })}</span></h3>
            <div className="remarks">
              { response?.remarkArray?.map((reason, index) => <Reason key={index} reason={reason} />) }
            </div>
          </div>
        );
    
        return null;
      }

      const LegalCompliance = () => {
    
        if (response?.legalComplianceArray?.length > 0) return (
          <div>
            <h3 className="dropdown red">{t('analysis.legalCompliance')} <span className="itemCount">{t('analysis.items', { count: response?.legalComplianceArray?.length || 0 })}</span></h3>
            <div className="issues">
              { response?.legalComplianceArray?.map((reason, index) => <Reason key={index} reason={reason} />) }
            </div>
          </div>
        );
    
        return null;
      }
        

      const BusinessConditions = () => {
    
        if (response?.businessConditionsArray?.length > 0) return (
          <div>
            <h3 className="dropdown yellow">{t('analysis.businessConditions')} <span className="itemCount">{t('analysis.items', { count: response?.businessConditionsArray?.length || 0 })}</span></h3>
            <div className="issues">
              { response?.businessConditionsArray?.map((reason, index) => { reason.law = null; return <Reason key={index} reason={reason} /> }) }
            </div>
          </div>
        );
    
        return null;
      }

      const Checklist = () => {
    
        if (response?.checklistArray?.length > 0) return (
          <div>
            <h3 className="dropdown purple">{"Playbook"} <span className="itemCount">{t('analysis.items', { count: response?.checklistArray?.length || 0 })}</span></h3>
            <div className="playbook">
              { response?.checklistArray?.map((reason, index) => <Reason key={index} reason={reason} />) }
            </div>
          </div>
        );
    
        return null;
      }
        
    
      const Deleted = () => {
    
        if (response?.deletedArray?.length > 0) return (
          <div>
            <h3 className="dropdown grey">{t('analysis.deleted')} <span className="itemCount">{t('analysis.items', { count: response?.deletedArray?.length || 0 })}</span></h3>
            <div className="deleted">
              { response?.deletedArray?.map((reason, index) => <Reason key={index} reason={reason} />) }
            </div>
          </div>
        );
    
        return null;
    }
    
    const NoIssues = () => {
    
        return (
          <div className="noIssues">
            {t('analysis.no_issues')}
          </div>
        );
    }

    function mapLanguage(language) {
      switch(language) {
        case "English":
          return "en";
        case "Français":
          return "fr";
        case "Nederlands":
          return "nl";
        case "Deutsch":
          return "de";
        default:
          return "en";
      }
    }

    function reverseMapLanguage(code) {
      switch(code) {
        case "en":
          return "English";
        case "fr":
          return "Français";
        case "nl":
          return "Nederlands";
        case "de":
          return "Deutsch";
        default:
          return "English";
      }
    }

    const config = useSelector(selectConfig);
    
    const AwaitingResults = () => {

      const { i18n } = useTranslation();

      function submitInfo() {

        setReviewing(true);
        if(!process.env.REACT_APP_IS_RESELLER) {
          setT(() => i18n.getFixedT(mapLanguage(languageState)))
        }

        if(!reviewing) {
          api.post('/set-review-info',
            { 
              documentId: id, 
              party: partyState,
              type: typeState,
              language: process.env.REACT_APP_IS_RESELLER ? reverseMapLanguage(i18n.language) : languageState,
              jurisdiction: jurisdictionState,
              trainingContext: selectedTrainingDocuments.map(d => d.toString()),
              checklist: checklist
            }
          )
        }
      }

      const SelectParty = () => {
        const options = parties?.map((party, index) => {
          return (
            <div className="partyOption" key={index}>
              <input 
                type="radio" 
                id={party} 
                name="party" 
                value={party} 
                onChange={(e) => setPartyState(e.target.value)}
                checked={partyState === party}
              />
              <label htmlFor={party}>{party}</label>
            </div>
          )
        });
      
        return (
          <div className="selectParty">
            <h3>{tGlobal('analysis.settings_3')}</h3>
            {options}
            <div className="partyOption" key="neutral">
              <input 
                type="radio" 
                id="neutral" 
                name="party" 
                value="Neutral"
                onChange={(e) => setPartyState("Neutral")}
                checked={partyState === "Neutral"}
              />
              <label htmlFor={"neutral"}>{tGlobal('analysis.settings_4')}</label>
            </div>
          </div>
        );
    }

    const [ localChecklist, setLocalChecklist ] = useState(checklist);

    const tooltipTargetRef = useRef();

    const insufficientCredits =  process.env.REACT_APP_IS_RESELLER && resellerConfig && resellerConfig?.credits?.balance < doc?.creditCost;

    if(!reviewing && parties.length === 0) {
      return <div className="nds">
          <div className="icon">
            <LottieAnimation animationData={contractAnimation} />
          </div>
          <h3>{tGlobal('analysis.nds_3')}</h3>
          <p>
            {tGlobal('analysis.nds_4')}
          </p>
        </div>
    };

    const unsupportedType = process.env.REACT_APP_IS_RESELLER && typeState === "Unknown";
    
    if(unsupportedType) {
      return <div className="nds settings">
        <p>{t('analysis.unsupported_type')}</p>
        <p><a href="mailto:legal@vdl.be">legal@vdl.be</a></p>
      </div>
    }

    if(!reviewing && parties.length > 0) {
      return <div className={"nds settings" + (insufficientCredits ? " blur" : "")}>
        { insufficientCredits && <CreditsInsufficient 
          amount={resellerConfig?.credits?.balance}
          requiredAmount={doc.creditCost}
          creditPurchaseUrl={resellerConfig.creditPurchaseUrl} 
        /> }
        <h2>{tGlobal('analysis.settings_1')}</h2>
        <p>{tGlobal('analysis.settings_2')}</p>
        { SelectParty() }
        { SelectType({
          type: typeState,
          setType: setTypeState,
          limitedList: process.env.REACT_APP_IS_RESELLER ? [typeState] : null,
        }) }
        { !process.env.REACT_APP_IS_RESELLER && SelectLanguage({language: languageState, setLanguage: setLanguageState}) }
        { SelectJurisdiction({jurisdiction: jurisdictionState, setJurisdiction: setJurisdictionState}) }
        { config.flags.checklist && !process.env.REACT_APP_IS_RESELLER &&
          <div className="checklist">
            <div className="info-wrapper">
              <h3>
                Playbook
              </h3>
              <div ref={tooltipTargetRef}>
                <ReactSVG className='info-icon' src={infoIcon} />
              </div>
            </div>
            <Tooltip targetRef={tooltipTargetRef} position={ETooltipPosition.TOP}>
                <div>{t('tooltip.playbook')}</div>
            </Tooltip>
            <MultipleQuestions
              questions={localChecklist}
              setQuestions={setLocalChecklist}
              submit={(e) => {setChecklist(localChecklist);}}
              enabled={true}
              name="Playbook"
            />
          </div> }
        <div className={"button " + (partyState && !reviewing && !insufficientCredits && !unsupportedType ? "enabled" : "disabled")} onClick={submitInfo}>
          {tGlobal('analysis.settings_7')} {process.env.REACT_APP_IS_RESELLER && doc?.creditCost && <sup>{doc.creditCost} credit{doc.creditCost > 1 ? "s" : ""}</sup>}
        </div>
      </div>
    }

    return <div className="nds">
        <div className="icon">
          <LottieAnimation animationData={contractAnimation} />
        </div>
        <h3>{tGlobal('analysis.nds_1')}</h3>
        <p>
          {tGlobal('analysis.nds_2')}
        </p>
      </div>
    };

    if(fetchingData) return null;

    const convertToCSV = () => {
      const { checklistArray, legalComplianceArray, businessConditionsArray } = response;
    
      const removeNonAsciiCharacters = (value) => {
        return value.replace(/[^\x00-\x7F]/g, '');  // This removes all non-ASCII characters
      };
    
      // Function to escape and quote values properly, and remove non-ASCII characters
      const escapeAndQuote = (value) => {
        if (value === null || value === undefined) {
          return '""';
        }
        const cleanedValue = removeNonAsciiCharacters(value.toString());
        return `"${cleanedValue.replace(/"/g, '""')}"`;
      };
    
      // Initialize CSV content
      let csv = '';
    
      // Helper function to add a section to the CSV
      const addSectionToCSV = (header, array) => {
        if (array.length > 0) {
          csv += escapeAndQuote(header) + '\r\n';
          array.forEach(item => {
            csv += escapeAndQuote(`${item.reason}\n${item.reasonExplained}`) + '\r\n';
          });
          csv += '\r\n'; // Add an empty row between sections
        }
      };
    
      // Add sections to CSV
      addSectionToCSV("Playbook", checklistArray);
      addSectionToCSV(t('analysis.legalCompliance'), legalComplianceArray);
      addSectionToCSV(t('analysis.businessConditions'), businessConditionsArray);
    
      return csv;
    };
  
    const downloadCSV = (csv: string, filename: string) => {
        const csvFile = new Blob([csv], { type: 'text/csv' });
        const downloadLink = document.createElement('a');
        downloadLink.download = filename;
        downloadLink.href = window.URL.createObjectURL(csvFile);
        downloadLink.style.display = 'none';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };

    const onDownloadAsCSV = () => {
        const csv = convertToCSV();
        downloadCSV(csv, `${doc.name.split('.')[0]} review.csv`);
    }

    return (
        <div className={'suggestions ' + (display ? "show" : "hide")}>
        {
          !finished ? (
            <AwaitingResults />
          ) : 
          (
            (response?.issueArray?.length || 0) === 0 && 
            (response?.remarkArray?.length || 0) === 0 && 
            (response?.deletedArray?.length || 0) === 0 && 
            (response?.legalComplianceArray?.length || 0) === 0 &&
            (response?.businessConditionsArray?.length || 0) === 0 &&
            (response?.checklistArray?.length || 0) === 0 &&
            <NoIssues />
          )
        }
        <Checklist />
        <Issues />
        <Remarks />
        <LegalCompliance />
        <BusinessConditions />
        <Deleted />
        {finished && <Button text={t("download_as_csv")} onClick={onDownloadAsCSV} />}
      </div>
    );
};

export default Analysis;